import styles from './NewPatroller.module.css';

import Alert from '@mui/material/Alert';
import Button from "@mui/material/Button";
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Icon from '@mui/material/Icon';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import PersonAdd from '@mui/icons-material/PersonAdd';
import ExpandMore from '@mui/icons-material/ExpandMore';
import ExpandLess from '@mui/icons-material/ExpandLess';

import React from "react";

import { getUser, addUser, completeUser } from "../../api.js";

import { useAuth } from "../../AuthContext";
import Divider from '@mui/material/Divider';

export const NewPatroller = ({ onNewPatroller }) => {
    const [ error, setError ] = React.useState(null);
    const [ loading, setLoading ] = React.useState(false);
    const [ success, setSuccess ] = React.useState(false);
    const [ patroller, setPatroller ] = React.useState({ firstName: '', lastName: '', email: '', sendWelcomeEmail: true });
    const [ emailError, setEmailError ] = React.useState(false);
    const [ isAdmin, setIsAdmin ] = React.useState(false);
    const [ isExpanded, setIsExpanded ] = React.useState(false);
    const [ errorCode, setErrorCode ] = React.useState(null);

    const { auth, token } = useAuth();

    React.useEffect(() => {
        const checkAdmin = async () => {
            const user = await getUser(auth.currentUser.uid, token);

            setIsAdmin(!!user.admin);
        };

        checkAdmin();
    }, [ auth, token ]);

    const handleOnChange = (e) => {
        // set email if valid format
        const { name, value, type, checked } = e.target;
        const fieldValue = type === 'checkbox' ? checked : value;
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

        if (name === 'email' && !emailRegex.test(value)) {
            setEmailError(true);
        } else {
            setEmailError(false);
        }

        setPatroller(prev => ({ ...prev, [ name ]: fieldValue }));
    };

    const handleOnCancel = () => {
        setPatroller({ firstName: '', lastName: '', email: '' });
        setError(null);
        setErrorCode(null);
        setEmailError(false);
        setSuccess(false);

        setIsExpanded(!isExpanded);
    };

    const finishAdd = (result) => {
        if (result.error) {
            setError(result.error);
            setErrorCode(result.code || null);
        } else {
            setError(null);
            setErrorCode(null);
            setPatroller({ firstName: '', lastName: '', email: '', sendWelcomeEmail: true });
            setSuccess(true);

            if (onNewPatroller) onNewPatroller(result);
        }
    };

    const submit = async (apiCall, keepPrompt = false) => {
        if (loading) return;  // Prevent double-submission

        setLoading(true);

        if (!keepPrompt) {
            setError(null);
            setErrorCode(null);
        }

        try {
            finishAdd(await apiCall(patroller, token));
        } finally {
            setLoading(false);
        }
    };

    const handleOnSave = () => submit(addUser);
    const handleOnComplete = () => submit(completeUser, true);

    return (
        isAdmin && <div className={ styles.container }>
            <div className={ styles.titleBar } onClick={ () => setIsExpanded(!isExpanded) }>
                <span>
                    <Icon component={ PersonAdd } sx={ { mr: 1 } } />
                    <Typography>Add New Patroller</Typography>
                </span>

                <span>
                    <Icon component={ isExpanded ? ExpandLess : ExpandMore } />
                </span>
            </div>

            <div className={ `${ styles.content } ${ isExpanded ? styles.expanded : styles.collapsed }` }>
                <div className={ styles.formRow }>
                    <div className={ styles.formField }>
                        <label htmlFor="firstName">First Name</label>
                        <input
                            type="text"
                            id="firstName"
                            name="firstName"
                            autoComplete='given-name'
                            value={ patroller.firstName || '' }
                            onChange={ handleOnChange }
                        />
                    </div>

                    <div className={ styles.formField }>
                        <label htmlFor="lastName">Last Name</label>
                        <input
                            type="text"
                            id="lastName"
                            name="lastName"
                            autoComplete='family-name'
                            value={ patroller.lastName || '' }
                            onChange={ handleOnChange }
                        />
                    </div>
                </div>

                <div className={ styles.formRow }>
                    <div className={ styles.formField }>
                        <label htmlFor="email">Email</label>
                        <input
                            type="email"
                            id="email"
                            name="email"
                            autoComplete='email'
                            value={ patroller.email }
                            onChange={ handleOnChange }
                            className={ emailError ? styles.error : patroller.email ? styles.verified : '' }
                        />

                        { emailError && <Alert variant="filled" severity="warning" className={ styles.error }>invalid email</Alert> }
                    </div>

                    <div className={ styles.formField }>
                        <span>&nbsp;</span>

                        <Tooltip title="A general welcome email that contains links to the tutorial videos on YouTube.">
                            <FormControlLabel
                                control={ <Checkbox
                                    checked={ patroller.sendWelcomeEmail }
                                    onChange={ handleOnChange }
                                    name="sendWelcomeEmail" /> }
                                label="Send welcome email"
                            />
                        </Tooltip>
                    </div>
                </div>

                <Alert severity="info" variant='filled'>New patrollers will still be required to verify their email address and set a password.</Alert>

                <Divider />

                <div className={ styles.buttonRow }>
                    <Button
                        variant="contained"
                        color="success"
                        onClick={ handleOnSave }
                        disabled={ !patroller.email || emailError }
                        loading={ loading }
                        loadingPosition="start">
                        Add
                    </Button>

                    <Button variant="outlined" color="warning" onClick={ handleOnCancel }>Cancel</Button>
                </div>

                { success && <Alert severity="success" variant='filled' onClose={ () => setSuccess(false) }>Patroller added successfully</Alert> }

                { error &&
                    <Alert
                        severity="error"
                        variant='filled'
                        action={ <Button color="primary" size="small" onClick={ () => { setError(null); setErrorCode(null); } }>close</Button> }
                    >
                        { error }
                    </Alert>
                }

                { error && errorCode === 'core/signin-only-account' &&
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={ handleOnComplete }
                        disabled={ loading }>
                        Complete this account
                    </Button>
                }
            </div>
        </div>
    );
};